import {
  Box,
  Typography,
  IconButton,
  Icon,
  useTheme,
  Tooltip,
  CircularProgress,
  Button,
} from '@mui/material';
import BottomSheet from '../BottomSheet';
import { Restaurant, Sentiment } from '../../models/restaurant';
import { useEffect, useState } from 'react';
import sentimentApi from '../../api/sentiment';
import restaurantApi from '../../api/restaurant';
import useUser from '../../state/useUser';
import CertifiedBadge from '../CertifiedBadge';

interface RestaurantDetailsBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  restaurant: Restaurant | null;
}

const RestaurantDetailsBottomSheet = ({
  isOpen,
  onClose,
  restaurant,
}: RestaurantDetailsBottomSheetProps) => {
  const theme = useTheme();
  const [user] = useUser();
  const [likedList, setLikedList] = useState<Restaurant[]>([]);
  const [isLiking, setIsLiking] = useState(false);

  useEffect(() => {
    if (!user) return;
    restaurantApi.getBySentiment(Sentiment.KeenToGo).then(setLikedList);
  }, [user, isOpen]);

  const isLiked = likedList.some((r) => r.id === restaurant?.id);

  const handleLike = async () => {
    if (!restaurant?.id) return;
    setIsLiking(true);
    await sentimentApi.create({ restaurantId: restaurant.id, sentiment: Sentiment.KeenToGo });
    const updated = await restaurantApi.getBySentiment(Sentiment.KeenToGo);
    setLikedList(updated);
    setIsLiking(false);
  };

  if (!restaurant) return null;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} maxWidth={600}>
      <Box p={2} position="relative">
        {isLiked && (
          <Icon
            sx={{
              position: 'absolute',
              top: 18,
              right: 64,
              color: theme.palette.success.main,
              fontSize: 32,
            }}
          >
            favorite
          </Icon>
        )}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">{restaurant.title}</Typography>
            {restaurant.isCertified && <CertifiedBadge size={24} />}
          </Box>
          <IconButton onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </Box>
        <Box
          component="img"
          src={restaurant.imageUrl}
          sx={{ width: '100%', height: 240, objectFit: 'cover', borderRadius: 2, mb: 2 }}
        />
        <Typography
          variant="body2"
          color={theme.palette.grey[400]}
          sx={{ display: 'flex', justifyContent: 'center', mb: 2, px: 2 }}
        >
          <Icon sx={{ mr: 1 }}>location_on</Icon>
          {restaurant.addressFull}
        </Typography>
        {Boolean(restaurant.quotes?.length) && (
          <Typography
            variant="body2"
            color={theme.palette.grey[400]}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
              px: 2,
              textAlign: 'center',
            }}
          >
            <i>
              "{restaurant.quotes[0].text}"{' '}
              {restaurant.quotes[0].author && `- ${restaurant.quotes[0].author}`}
            </i>
          </Typography>
        )}
        <Typography variant="body2" color={theme.palette.grey[600]} mt={1}>
          {restaurant.description}
        </Typography>
        {!isLiked && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
            <Tooltip title="Add to favourites">
              <Button variant="text" onClick={handleLike}>
                {isLiking ? (
                  <CircularProgress size={24} sx={{ color: theme.palette.success.main, mr: 2 }} />
                ) : (
                  <>
                    {`I'm keen to go`}
                    <Icon sx={{ color: theme.palette.success.main, fontSize: 32, ml: 2 }}>
                      favorite
                    </Icon>
                  </>
                )}
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>
    </BottomSheet>
  );
};

export default RestaurantDetailsBottomSheet;
